import { useEffect, useRef } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import {
  extractMermaidTextFromElement,
  looksLikeMermaid,
  MermaidBlock,
} from './MermaidDiagram'

type HtmlWithMermaidProps = {
  html: string
  className?: string
  /** Empty / missing content fallback HTML */
  emptyHtml?: string
}

function extractLang(el: Element): string | undefined {
  const fromClass = /(?:^|\s)language-(\w+)/.exec(el.className || '')?.[1]
  if (fromClass) return fromClass.toLowerCase()
  const dataLang = el.getAttribute('data-language') || el.getAttribute('data-lang')
  return dataLang?.toLowerCase() || undefined
}

export function findMermaidCandidates(root: HTMLElement): { host: HTMLElement; code: string }[] {
  const found: { host: HTMLElement; code: string }[] = []
  const seen = new Set<HTMLElement>()

  const consider = (host: HTMLElement, codeRaw: string, lang?: string) => {
    if (seen.has(host)) return
    const code = codeRaw.replace(/\u00a0/g, ' ')
    if (!looksLikeMermaid(code, lang)) return
    seen.add(host)
    found.push({ host, code })
  }

  root.querySelectorAll('pre').forEach((pre) => {
    const codeEl = pre.querySelector('code')
    const lang = extractLang(codeEl || pre)
    // innerText keeps Quill <br> as newlines (textContent drops them → glued tokens)
    const text = extractMermaidTextFromElement((codeEl as HTMLElement) || (pre as HTMLElement))
    consider(pre, text, lang)
  })

  root.querySelectorAll('code.language-mermaid').forEach((codeEl) => {
    if (codeEl.closest('pre')) return
    consider(codeEl as HTMLElement, extractMermaidTextFromElement(codeEl as HTMLElement), 'mermaid')
  })

  return found
}

/** Replace Mermaid code blocks in a container with rendered diagrams. Returns roots to unmount. */
export function mountMermaidBlocks(
  container: HTMLElement,
  idPrefix = 'post-mmd'
): Root[] {
  const roots: Root[] = []
  const mounts = findMermaidCandidates(container)
  for (const { host, code } of mounts) {
    const mountEl = document.createElement('div')
    mountEl.className = 'mermaid-html-mount'
    host.replaceWith(mountEl)
    const root = createRoot(mountEl)
    roots.push(root)
    root.render(<MermaidBlock code={code} idPrefix={idPrefix} />)
  }
  return roots
}

export function unmountMermaidRoots(roots: Root[]) {
  roots.forEach((r) => {
    try {
      r.unmount()
    } catch {
      /* ignore */
    }
  })
}

/**
 * Renders course POST HTML and auto-upgrades Mermaid code blocks to diagrams.
 * Detects: <pre>/<pre class="ql-syntax">, <code class="language-mermaid">, fenced ```mermaid.
 */
export default function HtmlWithMermaid({
  html,
  className,
  emptyHtml = '<p class="text-m2-on-surface-variant italic">Bài viết chưa có nội dung.</p>',
}: HtmlWithMermaidProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rootsRef = useRef<Root[]>([])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    unmountMermaidRoots(rootsRef.current)
    rootsRef.current = []

    const content = (html || '').trim() ? html : emptyHtml
    container.innerHTML = content
    rootsRef.current = mountMermaidBlocks(container, 'post-mmd')

    return () => {
      unmountMermaidRoots(rootsRef.current)
      rootsRef.current = []
    }
  }, [html, emptyHtml])

  return <div ref={containerRef} className={className} />
}
